// Web push disabled for now — not shipping while focused on /analytics web testing.
// Re-enable this import and the `build_web_push_browser_metadata(...)` call below together.
// import { build_web_push_browser_metadata } from '../web_push/web_push_browser';

function storage_is_available(storage_name) {
	try {
		const storage = window?.[storage_name];
		const test_key = '__data_hub_storage_test__';
		storage.setItem(test_key, test_key);
		storage.removeItem(test_key);
		return true;
	} catch {
		return false;
	}
}

function read_media_query(matches_query, true_value, false_value = null) {
	try {
		return window.matchMedia?.(matches_query)?.matches ? true_value : false_value;
	} catch {
		return false_value;
	}
}

function parse_lightweight_user_agent(user_agent) {
	if (!user_agent || typeof user_agent !== 'string') {
		return {};
	}
	const browser_match = user_agent.match(/(Edg|Chrome|Firefox|Version|Safari)\/([0-9.]+)/);
	const browser_token = browser_match?.[1] || null;
	const browser_name = browser_token === 'Edg'
		? 'Edge'
		: browser_token === 'Version'
			? 'Safari'
			: browser_token;
	const os = user_agent.includes('Windows')
		? 'Windows'
		: user_agent.includes('Mac OS X')
			? 'macOS'
			: user_agent.includes('Android')
				? 'Android'
				: user_agent.includes('iPhone') || user_agent.includes('iPad')
					? 'iOS'
					: user_agent.includes('Linux')
						? 'Linux'
						: null;
	const browser_engine = user_agent.includes('AppleWebKit')
		? 'WebKit'
		: user_agent.includes('Gecko/')
			? 'Gecko'
			: null;
	return {
		browser_name: browser_name || null,
		browser_version: browser_match?.[2] || null,
		browser_engine,
		os
	};
}

/**
 * Resolves init_completed_at / init_duration_ms once per analytics_state. Calling
 * build_browser_context on every flush would otherwise overwrite these with the time
 * of the latest flush, making init timing useless. We snapshot the first call and
 * reuse it. os_version, device_vendor, and device_model are intentionally left null
 * here for v1 — the backend can derive them from the user_agent string in a future
 * pass without forcing us to bundle a UA parser in the main script.
 */
function resolve_completed_init_timing(data_hub_instance) {
	const analytics_state = data_hub_instance?.analytics_state;
	if (!analytics_state) {
		return { init_started_at: null, init_completed_at: null, init_duration_ms: null };
	}
	if (!analytics_state.init_completed_at) {
		analytics_state.init_completed_at = new Date().toISOString();
		analytics_state.init_duration_ms = analytics_state.init_started_at
			? Math.max(0, Date.now() - Date.parse(analytics_state.init_started_at))
			: null;
	}
	return {
		init_started_at: analytics_state.init_started_at || null,
		init_completed_at: analytics_state.init_completed_at,
		init_duration_ms: analytics_state.init_duration_ms ?? null
	};
}

export function build_browser_context(data_hub_instance = window?.data_hub) {
	const connection = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection || {};
	const screen_orientation = window?.screen?.orientation || {};
	const user_agent = navigator?.userAgent || null;
	const parsed_user_agent = parse_lightweight_user_agent(user_agent);
	const { init_started_at, init_completed_at, init_duration_ms } = resolve_completed_init_timing(data_hub_instance);
	// Web push disabled — empty metadata keeps the web_push_* fields below as undefined
	// (dropped from the payload) instead of pulling the web_push module into the bundle.
	// const web_push_metadata = build_web_push_browser_metadata(data_hub_instance);
	const web_push_metadata = {};

	const lib_version_string = () => {
		try {
			return window?.DataHub?.version != null ? String(window.DataHub.version) : null;
		} catch (e) {
			return null;
		}
	};

	return {
		user_agent,
		browser_name: parsed_user_agent.browser_name || null,
		browser_version: parsed_user_agent.browser_version || null,
		browser_engine: parsed_user_agent.browser_engine || null,
		os: parsed_user_agent.os || null,
		os_version: null,
		device_type: navigator?.maxTouchPoints > 0 ? 'touch' : 'desktop',
		device_vendor: null,
		device_model: null,
		viewport_width: window?.innerWidth || null,
		viewport_height: window?.innerHeight || null,
		screen_width: window?.screen?.width || null,
		screen_height: window?.screen?.height || null,
		screen_available_width: window?.screen?.availWidth || null,
		screen_available_height: window?.screen?.availHeight || null,
		device_width: window?.screen?.width || null,
		device_height: window?.screen?.height || null,
		device_pixel_ratio: window?.devicePixelRatio || null,
		color_depth: window?.screen?.colorDepth || null,
		pixel_depth: window?.screen?.pixelDepth || null,
		orientation_type: screen_orientation.type || null,
		orientation_angle: screen_orientation.angle ?? null,
		language: navigator?.language || null,
		languages: Array.isArray(navigator?.languages) ? Array.from(navigator.languages) : [],
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
		timezone_offset_minutes: new Date().getTimezoneOffset(),
		hardware_concurrency: navigator?.hardwareConcurrency || null,
		device_memory_gb: navigator?.deviceMemory || null,
		max_touch_points: navigator?.maxTouchPoints || 0,
		cookies_enabled: navigator?.cookieEnabled ?? null,
		local_storage_available: storage_is_available('localStorage'),
		session_storage_available: storage_is_available('sessionStorage'),
		do_not_track: navigator?.doNotTrack === '1',
		online: navigator?.onLine ?? null,
		connection_effective_type: connection.effectiveType || null,
		connection_downlink_mbps: connection.downlink ?? null,
		connection_rtt_ms: connection.rtt ?? null,
		connection_save_data: connection.saveData ?? null,
		prefers_reduced_motion: read_media_query('(prefers-reduced-motion: reduce)', true, false),
		prefers_color_scheme: read_media_query('(prefers-color-scheme: dark)', 'dark', 'light'),
		webdriver: navigator?.webdriver || false,
		visibility_state: document?.visibilityState || null,
		lib: 'data_hub_web_js',
		lib_version: lib_version_string() || null,
		init_started_at,
		init_completed_at,
		init_duration_ms,
		consent_state: window?.data_hub_consent === false ? 'denied' : 'granted',
		web_push_consent_state: web_push_metadata.web_push_consent_state,
		notification_permission: web_push_metadata.notification_permission,
		notification_api_supported: web_push_metadata.notification_api_supported,
		service_worker_supported: web_push_metadata.service_worker_supported,
		web_push_manager_supported: web_push_metadata.web_push_manager_supported,
		web_push_supported: web_push_metadata.web_push_supported,
		web_push_secure_context: web_push_metadata.web_push_secure_context,
		web_push_prompt_available: web_push_metadata.web_push_prompt_available,
		web_push_origin_allowed: web_push_metadata.web_push_origin_allowed,
		web_push_origin_status: web_push_metadata.web_push_origin_status,
		web_push_state: web_push_metadata.web_push_state,
		web_push_subscription_active: web_push_metadata.web_push_subscription_active,
		web_push_last_registered_at: web_push_metadata.web_push_last_registered_at,
		web_push_last_opt_out_at: web_push_metadata.web_push_last_opt_out_at,
		web_push_last_error_type: web_push_metadata.web_push_last_error_type
	};
}
